
import React from 'react';
import {Button, Col, Row, Space, Typography} from 'antd';
import {
    CalendarOutlined,
    ClockCircleOutlined,
    FileOutlined,
    PhoneOutlined,
    ProfileOutlined,
    TeamOutlined,
    UserOutlined
} from '@ant-design/icons';
import {weekdayNames} from "@/pages/student/lessons/take/_section/LessonCard";
import {myAcademy} from "@/lib/services/academyService";
import {MapIcon, PrinterIcon} from "lucide-react";
import {sprintf} from "@/lib/utils/sprintf";
import {URLS} from "@/lib/urls";
import {Link} from "react-router-dom";
import {useReactToPrint} from "react-to-print";
import RenderIf from "@/components/render-if";
import {convertToJalaliWithFormat} from "@/lib/utils/date";

const {Title, Text} = Typography;

const TeacherPrintListHeader = ({classInfo, contentRef}) => {
    const reactToPrintFn = useReactToPrint({contentRef});
    const myAcademyData = myAcademy();

    return (
        <>
            <div className="absolute top-4 right-4 print:hidden">
                <Link to={sprintf(URLS.teacher.lessons.detail, {id: classInfo?.id})}>
                    <Button>بازگشت</Button>
                </Link>

                <Button
                    className="mx-2 top-2"
                    onClick={reactToPrintFn}
                >
                    <PrinterIcon/>
                </Button>
            </div>

            <div
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm mb-6"
                style={{
                    printColorAdjust: 'exact',
                    WebkitPrintColorAdjust: 'exact'
                }}
            >
                <div
                    className="rounded-t-xl text-white"
                    style={{
                        padding: '10px 20px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}
                >
                    <Row align="middle" gutter={[24, 16]}>
                        <Col xs={24} sm={8} md={6}>
                            <div
                                className="inline-block rounded-xl bg-white p-1.5 shadow-md"
                            >
                                <div
                                    className="flex items-center justify-center mx-auto"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '10px'
                                    }}
                                >
                                    <img
                                        src={myAcademyData?.logo}
                                        className="p-2 rounded-lg"
                                        style={{borderRadius: '17px'}}
                                        alt={myAcademyData?.name}
                                    />
                                </div>
                            </div>
                        </Col>

                        <Col xs={24} sm={16} md={18}>
                            <Title
                                level={2}
                                style={{
                                    color: '#ffffff',
                                    margin: 0,
                                    marginBottom: '8px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {myAcademyData?.name}
                            </Title>

                            <Text
                                style={{
                                    color: 'rgba(255,255,255,0.9)',
                                    fontSize: '14px',
                                    display: 'block'
                                }}
                            >
                                <PhoneOutlined style={{marginLeft: '16px', marginRight: '8px'}}/>
                                {myAcademyData?.phone}
                            </Text>

                            <Text
                                style={{
                                    color: 'rgba(255,255,255,0.85)',
                                    fontSize: '13px',
                                    display: 'block'
                                }}
                            >
                                <div className="flex">
                                    <MapIcon
                                        className="w-4"
                                        style={{fontSize: '5px', marginLeft: '16px', marginRight: '8px'}}
                                    />
                                    <div>{myAcademyData?.address}</div>
                                </div>
                            </Text>
                        </Col>
                    </Row>
                </div>

                <div className="p-2.5">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={17}>
                            <div
                                className="h-full border-r border-gray-200 dark:border-gray-700 pr-6"
                            >
                                <Space direction="vertical" size="middle" style={{width: '100%'}}>
                                    <div>
                                        <Text
                                            strong
                                            className="dark:!text-gray-300"
                                            style={{width: '120px', display: 'inline-block', color: '#555'}}
                                        >
                                            <ProfileOutlined/> نام کلاس:
                                        </Text>
                                        <Text className="dark:!text-gray-100" style={{color: '#333'}}>
                                            {classInfo?.topic?.title}
                                        </Text>
                                    </div>

                                    <div>
                                        <Text
                                            strong
                                            className="dark:!text-gray-300"
                                            style={{width: '120px', display: 'inline-block', color: '#555'}}
                                        >
                                            <UserOutlined/> استاد:
                                        </Text>
                                        <Text className="dark:!text-gray-100" style={{color: '#333'}}>
                                            {(classInfo?.teacher_detail?.firstname ?? '') + ' ' + (classInfo?.teacher_detail?.lastname ?? '')}
                                        </Text>
                                    </div>

                                    <div>
                                        <Text
                                            strong
                                            className="dark:!text-gray-300"
                                            style={{width: '120px', display: 'inline-block', color: '#555'}}
                                        >
                                            <TeamOutlined/> مقطع:
                                        </Text>
                                        <Text className="dark:!text-gray-100">
                                            {classInfo?.topic?.grade?.title}
                                        </Text>
                                    </div>

                                    <div>
                                        <Text
                                            strong
                                            className="dark:!text-gray-300"
                                            style={{width: '120px', display: 'inline-block', color: '#555'}}
                                        >
                                            <CalendarOutlined/> ترم تحصیلی:
                                        </Text>
                                        <Text className="dark:!text-gray-100" style={{color: '#333'}}>
                                            {classInfo?.semester?.title}
                                        </Text>
                                    </div>

                                    <RenderIf condition={classInfo?.title}>
                                        <div>
                                            <Text
                                                strong
                                                className="dark:!text-gray-300"
                                                style={{width: '120px', display: 'inline-block', color: '#555'}}
                                            >
                                                <FileOutlined/> توضیحات:
                                            </Text>
                                            <Text className="dark:!text-gray-100" style={{color: '#333'}}>
                                                {classInfo?.title}
                                            </Text>
                                        </div>
                                    </RenderIf>
                                </Space>
                            </div>
                        </Col>

                        <Col xs={24} md={7}>
                            <div
                                className="grid grid-cols-2 gap-3"
                            >
                                {classInfo?.times_detail.map((item, index) => (
                                    <div
                                        key={index}
                                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1.5 transition-all"
                                    >
                                        <ClockCircleOutlined
                                            style={{color: '#9b70cc', marginLeft: '6px'}}
                                        />

                                        <Text
                                            strong
                                            className="dark:!text-gray-200"
                                            style={{marginLeft: '4px', color: '#495057', fontSize: '12px'}}
                                        >
                                            <RenderIf condition={item?.weekday}>
                                                {weekdayNames[item?.weekday]}:
                                            </RenderIf>
                                            <RenderIf condition={item?.date}>
                                                {convertToJalaliWithFormat(item?.date)}
                                            </RenderIf>
                                        </Text>

                                        <Text
                                            className="dark:!text-gray-400"
                                            style={{
                                                color: '#6c757d',
                                                float: 'left',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {item?.start} - {item?.end}
                                        </Text>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </div>

                <div
                    className="rounded-b-xl border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2.5 py-2.5"
                >
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Text type="secondary" className="dark:!text-gray-400" style={{fontSize: '12px'}}>
                                تاریخ چاپ: {new Date().toLocaleDateString('fa-IR')}
                            </Text>
                        </Col>

                        <Col>
                            <Text type="secondary" className="dark:!text-gray-400" style={{fontSize: '12px'}}>
                                {window.location.href}
                            </Text>
                        </Col>

                        <Col>
                            <Text type="secondary" className="dark:!text-gray-400" style={{fontSize: '14px'}}>
                                کد کلاس: <b>{classInfo?.code}</b>
                            </Text>
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    );
};

export default TeacherPrintListHeader;
